/**
 * How a deliverable maps to a row in generation_costs.
 *
 * Shared by the studio app and generate-format-variant on purpose. They each
 * derived this key separately and disagreed: the client turned "5K" into
 * `format_5K`, which is not a row, so it fell back to 10 credits and showed
 * "~10 credits" on the tile — while the server turned the same request into
 * `upscale_5k` and charged 25. The customer was quoted one number and billed
 * another.
 *
 * "Auto" had the same shape of fault, differing only in case.
 *
 * One derivation, both sides. A price shown and a price charged cannot drift
 * apart if they are computed by the same function.
 */

/**
 * The sizes offered as upscales rather than aspect ratios.
 *
 * 2K and 4K, because those are sizes Gemini can produce. 3K and 5K are
 * deliberately absent: anything above 2048px renders at 4K, so a "5K" tier
 * charged 25 credits for the same image the 4K tier sells at 20, and "3K"
 * charged 15 for it. Do not add a tier here without a size the model
 * actually outputs.
 */
export const UPSCALE_RATIOS = ["2K", "4K"] as const;

export function isUpscaleRatio(ratio: string): boolean {
  return (UPSCALE_RATIOS as readonly string[]).includes(ratio);
}

/**
 * The rate-card key for a deliverable.
 *
 * Pass what the user picked. An upscale is keyed by its size, everything else
 * by its aspect ratio with the colon flattened — `format_16x9`, `upscale_4k`,
 * `format_auto`.
 */
export function deliverableActionKey(ratio: string): string {
  const clean = String(ratio || "auto").trim();
  if (isUpscaleRatio(clean)) {
    return `upscale_${clean.toLowerCase()}`;
  }
  return `format_${clean.toLowerCase().replace(/[^a-z0-9]/g, "x")}`;
}

/**
 * What a model tier does to the price.
 *
 * The rate card prices the *output* — studio_1k is 20 credits — but the model
 * menu offered Pro and Flash Lite at that same price, so a customer could pick
 * the most expensive model for free and the cheapest was no cheaper. The
 * multiplier keeps one row per action while letting the tier carry its cost:
 * Lite half price, Pro double, Flash the baseline.
 *
 * Matched on the id substring the way videoCostKey already is, so a model
 * rename cannot silently reprice; an unrecognised image model is the baseline.
 */
export function modelCostMultiplier(model?: string | null): number {
  const id = String(model || "");
  if (id.includes("lite")) return 0.5;
  if (id.includes("pro")) return 2;
  return 1;
}

/** The credits an action costs on a given model. Whole credits, rounded up. */
export function costWithModel(baseCredits: number, model?: string | null): number {
  return Math.ceil(baseCredits * modelCostMultiplier(model));
}
